import { getAllUsers } from "api/graphQL_users";
import { useEffect, useState, useContext, useRef, useCallback } from "react";
import CreateUserModal from "./components/CreateUserModal";
import UsersTable from "./components/UsersList";
import { getAllOrganizationsSecond } from "api/graphQL_organizations";
import { getRoleDrop } from "api/role";
import { ClientContext } from "ClientProvider";
import { useLocation, useNavigate } from "react-router-dom";

export type UserRowObj = {
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  last_sign_in_at: string;
  created_at: string;
  updated_at: string;
  id: string;
  password: string;
  organizations: any;
  auth_2fa: boolean;
  allOrganizations: [];
};

const Users = () => {
  const [users, setUsers] = useState<UserRowObj[]>([]);
  const [isDataLoading, setIsDataLoading] = useState<boolean>();
  const [page, setPage] = useState<number>(1);
  const [totalpage, setTotalPage] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [orgPage] = useState<number>(1);
  const [orgPageSize] = useState<number>(500);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [roleDataDrop, setRoleDataDrop] = useState<any>([]);
  const [organizations, setOrganizations] = useState<any>([]);
  const { singleRoleData } = useContext(ClientContext);
  const [searchTexts, setSearchTexts] = useState<string>("");
  const location = useLocation();
  const navigate = useNavigate();

  const fetchOrganizations = useCallback(() => {
    getAllOrganizationsSecond(orgPage, orgPageSize, "")
      .then((data) => {
        setOrganizations(
          data?.[0]?.map((ele: any) => ({ ...ele, id: ele?.id }))
        );
      })
      .catch(() => {});
  }, [orgPage, orgPageSize]);

  const prevValues = useRef<{ searchTexts: string }>({
    searchTexts: "__init__",
  });

  const fetchUsers = useCallback(() => {
    setIsDataLoading(true);
    if (pageSize === null || pageSize === undefined) {
      setIsDataLoading(false);
      return;
    }
    let finalSearchText = "";
    if (location.state) {
      if (location.state?.search) {
        finalSearchText = location.state.search;
        const searchTexts = location.state.search;
        prevValues.current = { searchTexts };
        navigate(".", { replace: true, state: null });
      }
    } else {
      finalSearchText = searchTexts;
    }
    const params = { page, per_page: pageSize, searchTexts: finalSearchText };
    getAllUsers(params)
      .then((data) => {
        setUsers(data?.users ?? []);
        setTotalPage(data.total_pages);
        setTotalItems(data.total_items);
        setCurrentPage(data.current_page);
      })
      .catch(() => {})
      .finally(() => setIsDataLoading(false));
  }, [page, pageSize, searchTexts, location.state, navigate]);

  const fetchRoleDrop = useCallback(() => {
    getRoleDrop()
      .then((data) => {
        setRoleDataDrop(data?.length > 0 ? data : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const hasChanged = prevValues.current.searchTexts !== searchTexts;
    if (!hasChanged) return;
    fetchUsers();
  }, [page, searchTexts]);

  useEffect(() => {
    fetchOrganizations();
    fetchRoleDrop();
  }, [fetchOrganizations, fetchRoleDrop]);

  const serchboxValueChange = async (searchTerm: string) => {
    const search_txt = searchTerm;
    if (search_txt !== searchTexts && searchTexts !== "") {
      setSearchTexts(search_txt);
      setPage(1);
      setCurrentPage(1);
    } else {
      setSearchTexts(search_txt);
    }
  };

  useEffect(() => {
    setPageSize(50);
  }, [searchTexts]);

  return (
    <div className="usr-root">
      {/* Page header */}
      <div className="usr-page-header">
        <div className="usr-page-title-block">
          <div className="usr-page-icon">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div>
            <h1 className="usr-page-heading">Users</h1>
            <p className="usr-page-sub">
              Manage users, roles and organization assignments
            </p>
          </div>
        </div>
        {/* <div className="usr-page-actions">
          <CreateUserModal
            fetchUsers={fetchUsers}
            organizations={organizations}
            roleDataDrop={roleDataDrop}
            roleData={singleRoleData}
          />
        </div> */}
      </div>

      {/* Table */}
      <UsersTable
        pageSize={pageSize}
        setPageSize={setPageSize}
        tableData={users}
        fetchUsers={fetchUsers}
        isLoading={isDataLoading}
        page={page}
        setPage={setPage}
        totalpage={totalpage}
        totalItems={totalItems}
        currentPage={currentPage}
        organizations={organizations}
        roleData={singleRoleData}
        roleDataDrop={roleDataDrop}
        onValueChange={serchboxValueChange}
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .usr-root {
          font-family: 'Plus Jakarta Sans', sans-serif;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .usr-page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
        }
        .usr-page-title-block { display: flex; align-items: center; gap: 14px; }
        .usr-page-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: linear-gradient(135deg, #f0fdf4, #dcfce7);
          border: 1.5px solid #bbf7d0;
          display: flex; align-items: center; justify-content: center;
          color: #16a34a; flex-shrink: 0;
        }
        .usr-page-heading {
          font-size: 20px; font-weight: 800; color: #0f172a;
          letter-spacing: -0.4px; margin-bottom: 2px; line-height: 22px;
        }
        .usr-page-sub { font-size: 13px; color: #94a3b8; font-weight: 500; }
        .usr-page-actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }

        @media (max-width: 600px) {
          .usr-root { padding: 14px; gap: 14px; }
          .usr-page-header { flex-direction: column; align-items: flex-start; }
        }
      `}</style>
    </div>
  );
};

export default Users;